import {Insurer} from '../../insurer';
import {TextTranslations} from '../../question';
import {Version} from '../../version';
import {Category, CategoryCode} from '../category';
import {Duration} from '../duration';
import {JobRiskClass} from '../job-risk-class';
import {RiderType} from '../person-insurance';
import {
  HUMANIA_EMMA_DURATIONS,
  HUMANIA_EMMA_MAX_TOTAL_COVERAGE_ELITE,
  HUMANIA_EMMA_MAX_TOTAL_COVERAGE_STANDARD,
  HUMANIA_EMMA_MAX_TOTAL_COVERAGE_SUPERIOR,
  HUMANIA_EMMA_V2_DURATIONS,
  HUMANIA_EMMA_V2_MAX_COVERAGE,
} from './humania-emma-constants';
import {HUMANIA_EMMA_JOB_CATEGORY_ID_TO_RISK_CLASS} from './humania-emma-job-to-risk-class';

export enum ProviderId {
  HUMANIA_EMMA = 'humania-emma',
  HUMANIA_EMMA_V2 = 'humania-emma-v2',
  EXTERNAL = 'external',
  MOCK = 'mock',
}

export const ACTIVE_PROVIDERS = [ProviderId.HUMANIA_EMMA_V2];

export interface LifeInsuranceProvider {
  id: ProviderId;
  insurer: Insurer;
  categories: Category[];
  durations: Duration[];
  name: TextTranslations;
  phone: string;
  address: TextTranslations;
  riderTypes?: RiderType[];
  jobCategoryIdToRiskClass?: Record<number, JobRiskClass>;
  latestVersion: ProviderVersion;
}

export const HUMANIA_EMMA_PROVIDER: Readonly<LifeInsuranceProvider> = {
  id: ProviderId.HUMANIA_EMMA,
  insurer: Insurer.HUMANIA,
  categories: [
    {
      code: CategoryCode.C1,
      maxCoverage: HUMANIA_EMMA_MAX_TOTAL_COVERAGE_ELITE,
      name: {en: 'Elite', fr: 'Élite'},
    },
    {
      code: CategoryCode.C2,
      maxCoverage: HUMANIA_EMMA_MAX_TOTAL_COVERAGE_SUPERIOR,
      name: {en: 'Superior', fr: 'Supérieur'},
    },
    {
      code: CategoryCode.C3,
      maxCoverage: HUMANIA_EMMA_MAX_TOTAL_COVERAGE_STANDARD,
      name: {en: 'Standard', fr: 'Standard'},
    },
  ],
  durations: HUMANIA_EMMA_DURATIONS,
  name: {en: 'Humania Assurance inc', fr: 'Humania Assurance inc'},
  phone: '8007738404',
  address: {
    en: '1555, Girouard Ouest street, Saint-Hyacinthe (Quebec), J2S 2Z6',
    fr: "1555, boul. de l'Avenir, bureau 306, Laval, QC H7S 2N5",
  },
  riderTypes: [RiderType.CHILD],
  latestVersion: {
    healthQuestionnaireVersion: Version.V1,
    ratesVersion: Version.V1,
  },
};

export const HUMANIA_EMMA_V2_PROVIDER: Readonly<LifeInsuranceProvider> = {
  id: ProviderId.HUMANIA_EMMA_V2,
  insurer: Insurer.HUMANIA,
  categories: [
    {
      code: CategoryCode.C1,
      maxCoverage: HUMANIA_EMMA_V2_MAX_COVERAGE,
      name: {en: 'Eligible', fr: 'Éligible'},
    },
  ],
  durations: HUMANIA_EMMA_V2_DURATIONS,
  name: {en: 'Humania Assurance inc', fr: 'Humania Assurance inc'},
  phone: '8007738404',
  address: {
    en: '1555, Girouard Ouest street, Saint-Hyacinthe (Quebec), J2S 2Z6',
    fr: "1555, boul. de l'Avenir, bureau 306, Laval, QC H7S 2N5",
  },
  riderTypes: [RiderType.CHILD, RiderType.CANCER, RiderType.DISABILITY],
  jobCategoryIdToRiskClass: HUMANIA_EMMA_JOB_CATEGORY_ID_TO_RISK_CLASS,
  latestVersion: {
    healthQuestionnaireVersion: Version.V2,
    ratesVersion: Version.V2,
  },
};

export const MOCK_PROVIDER: Readonly<LifeInsuranceProvider> = {
  id: ProviderId.MOCK,
  insurer: Insurer.HUMANIA,
  categories: [{code: CategoryCode.C1, maxCoverage: 1_000_000, name: {en: 'SOME_NAME', fr: 'UN_NOM'}}],
  durations: [Duration.T50],
  name: {en: 'Mock Provider', fr: 'Fournisseur fictif'},
  phone: '8001234567',
  address: {en: '123 Mock St, Mock City, Mock Province, M0C 0K0', fr: '123 rue Mock'},
  jobCategoryIdToRiskClass: {1: JobRiskClass['4A'], 2: JobRiskClass.INELIGIBLE},
  riderTypes: [RiderType.CHILD],
  latestVersion: {
    healthQuestionnaireVersion: Version.VX,
    ratesVersion: Version.VX,
  },
};

export const LifeInsuranceProviders: Map<ProviderId, LifeInsuranceProvider> = new Map([
  [ProviderId.HUMANIA_EMMA, HUMANIA_EMMA_PROVIDER],
  [ProviderId.HUMANIA_EMMA_V2, HUMANIA_EMMA_V2_PROVIDER],
  [ProviderId.MOCK, MOCK_PROVIDER],
]);

export interface ProviderVersion {
  healthQuestionnaireVersion: Version;
  ratesVersion: Version;
}

export function getLifeInsuranceProvider(providerId: ProviderId): LifeInsuranceProvider {
  const provider = LifeInsuranceProviders.get(providerId);
  if (!provider) {
    throw new Error(`Provider with id ${providerId} not found.`);
  }
  return provider;
}
